import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useFujiAuth } from '../../../auth/useFujiAuth';
import Header from '../../../components/header/Header';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import BoughtTicketList from '../../../components/BoughtTicketList/BoughtTicketList';
import BorderButton from '../../../components/BorderButton/BorderButton';
import { CacheManager } from '../../../utils/CacheManager/CacheManager';
import { showNotificationDialog } from '../../../utils';
import { isUsableTicket } from '../../../utils';
import { localStorageKeys, ONLINE_FETCH_TIMEOUT, ORDER_STATUS, TIMEOUT_DURATION } from '../../../utils/Constants';
import useR05Api from '../../../api/useR05Api';
import TextBreaks from '../../../components/TextBreaks/TextBreaks';
import { ErrorType, OrderTicketList } from 'types';

/**
 * 使用済みチケット一覧画面
 */
const UsedTicketListScreen = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [tickets, setTickets] = useState<OrderTicketList[]>([]);
    const [hasNetWorkError, setHasNetWorkError] = useState(false);
    const { isAuthenticated } = useFujiAuth();
    const location = useLocation();
    const { fetchOrderList } = useR05Api();
    const navigate = useNavigate();

    /**
     * エラーが発生した時のNotification
     * @param {Error} error
     */
    const showErrorNotification = useCallback(
        (error: ErrorType) => {
            if (String(error).includes('offline')) {
                showNotificationDialog('error', '', t('notification.title'), t('notification.offline'));
            } else {
                showNotificationDialog('error', '', t('notification.title'), t('notification.failed'));
            }
        },
        [t],
    );

    /**
     * 使用前チケットの取得
     */
    const fetchOrderTickets = useCallback(async () => {
        setLoading(true);
        let onlineFetchTimeoutId;
        const totalFetchTimeoutId = setTimeout(() => {
            setLoading(false);
        }, TIMEOUT_DURATION);

        const cacheName = 'used_ticket_orders_cache';
        const cacheUrl = `${location.pathname}/ticketOrders.json`;
        const cacheManager = new CacheManager(cacheName, cacheUrl);

        // オンライン時のfetch
        const fetchOnline = async () => {
            let orderTickets = (
                await fetchOrderList({
                    status: [
                        ORDER_STATUS.BOUGHT,
                        ORDER_STATUS.NORMAL_PENDING,
                        ORDER_STATUS.NORMAL_REFUND,
                        ORDER_STATUS.WEB_PENDING,
                        ORDER_STATUS.WEB_REFUND,
                        ORDER_STATUS.USER_REFUND_PENDING,
                        ORDER_STATUS.STOCK_USER_REFUND_PENDING,
                        ORDER_STATUS.USER_REFUNDED,
                        ORDER_STATUS.STOCK_USER_REFUNDED,
                    ].join(','),
                })
            )?.filter((ticket) => !isUsableTicket(ticket));
            orderTickets = orderTickets?.map((item) => {
                // 販売チケット名のキーを分離
                return { ...item, saleTicketName: item?.ticketName };
            });
            setTickets(orderTickets ?? []);
            await cacheManager.saveToCache(orderTickets ?? []);
        };

        // オフライン or 低速時のfetch
        const fetchOffline = async () => {
            setHasNetWorkError(true);
            showNotificationDialog('error', '', t('notification.title'), t('notification.network-failed'));
            const response = await cacheManager.readFromCache();
            setTickets(response);
        };

        try {
            if (navigator.onLine) {
                // 低速時の処理
                onlineFetchTimeoutId = setTimeout(() => {
                    void (async () => {
                        try {
                            await fetchOffline();
                        } catch {}
                        setLoading(false);
                    })();
                }, ONLINE_FETCH_TIMEOUT);
                await fetchOnline();
                setLoading(false);
                clearTimeout(onlineFetchTimeoutId);
            } else {
                try {
                    await fetchOffline();
                } catch {
                    showNotificationDialog('error', '', t('notification.title'), t('ticket-display.no-ticket-cache'));
                }
                setLoading(false);
            }
            clearTimeout(totalFetchTimeoutId);
        } catch (error) {
            setLoading(false);
            showErrorNotification(error as ErrorType);
            clearTimeout(onlineFetchTimeoutId);
            clearTimeout(totalFetchTimeoutId);
        }
    }, [fetchOrderList, location, showErrorNotification, t]);

    useEffect(() => {
        if (isAuthenticated) {
            void fetchOrderTickets();
        }
    }, [fetchOrderTickets]);

    /**
     * リロードボタンを押した時の挙動
     */
    const onTapRefetch = useCallback(() => {
        void fetchOrderTickets();
    }, [fetchOrderTickets]);

    /**
     * ログイン後にこの画面に戻れるようにlocalStorageに保持する
     */
    const handleClickToSignIn = useCallback(() => {
        localStorage.setItem(
            localStorageKeys.LOGIN_FLOW,
            JSON.stringify({
                path: '/bought/used',
            }),
        );
        navigate('/auth');
    }, [navigate]);

    return (
        <>
            {loading && <CenterLoading />}
            <Header title={t('header.used-ticket')} refetch={onTapRefetch} />
            {isAuthenticated ? (
                tickets.length > 0 ? (
                    <div className="content-container scrollable">
                        <BoughtTicketList ticketList={tickets} />
                    </div>
                ) : (
                    <div className="no-ticket-view">
                        <TextBreaks
                            text={
                                !hasNetWorkError
                                    ? loading
                                        ? t('loading.small-bought-ticket-list')
                                        : t('ticket-display.no-ticket')
                                    : t('ticket-display.no-ticket-cache')
                            }
                        />
                    </div>
                )
            ) : (
                <div className="no-ticket-view">
                    <TextBreaks text={t('notification.authentication-failed')} />
                    <BorderButton onClick={handleClickToSignIn} text={t('button.to-sign-in')} />
                </div>
            )}
        </>
    );
};

export default UsedTicketListScreen;
